import {
  Component,
  EventEmitter,
  Output,
  Input,
  SimpleChanges,
  Renderer2,
  OnInit,
  OnChanges,
  OnDestroy,
  ElementRef,
} from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';

import {
  PathElement,
  NodeEntry,
  Node,
  ResultSetPaging,
} from '@alfresco/js-api';
import { TreeBaseNode } from '@alfresco/adf-content-services';
import { ContentApiService } from '@alfresco/aca-shared';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { TreeViewDataSource } from '../../../sidenav/custom-tree-view/data/tree-view-datasource';
import {
  NodeInfoService,
  WkTreeViewService,
  InsertMediaService,
} from '../../../../services';
import { ContentService } from '../../../shared/services/content.service';
import { CommonConstants } from '../../../../shared/common.constant';

@Component({
  selector: 'wk-media-tree-view-list',
  templateUrl: './wk-custom-tree-view.component.html',
  styleUrls: ['./wk-custom-tree-view.component.scss'],
  standalone: false,
})
export class WkMediaTreeViewComponent implements OnInit, OnChanges, OnDestroy {
  /** Identifier of the node to display. */
  @Input()
  node: Node;

  @Input()
  mediaType: string;

  @Input()
  forbiddenType: string;

  @Input()
  currentSite: string;

  /** Emitted when a node in the tree view is clicked. */
  @Output()
  nodeClicked: EventEmitter<NodeEntry> = new EventEmitter();

  /** Emitted when an invalid node id is given. */
  @Output()
  treeError: EventEmitter<any> = new EventEmitter();

  public treeControl: FlatTreeControl<TreeBaseNode>;
  public dataSource: TreeViewDataSource;
  public nodeId: string;
  public onDestroy$ = new Subject<void>();
  private parentElements: PathElement[];
  private nodeCollapsed = false;
  private selectedMediaNodes = [];
  private isTravered = false;
  public audio: HTMLAudioElement;
  private previousNode = Object.create(null);
  private lastSelectedAudioVideo;
  private readonly rootName = 'Sites';
  constructor(
    private readonly treeViewService: WkTreeViewService,
    private readonly contentApiService: ContentApiService,
    private readonly renderer: Renderer2,
    private readonly eleRef: ElementRef,
    private readonly insertMediaService: InsertMediaService,
    private readonly nodeService: NodeInfoService,
    private readonly contentService: ContentService
  ) {
    this.treeControl = new FlatTreeControl<TreeBaseNode>(
      this.getLevel,
      this.isExpandable
    );
    this.parentElements = [];
    this.audio = new Audio();
  }

  ngOnInit() {
    this.insertMediaService.selectedNode.clear();
    this.currentSite = localStorage.getItem('currentSite');
    this.dataSource?.dataChange
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        if (
          this.parentElements.length > 0 &&
          this.treeControl.dataNodes &&
          !this.nodeCollapsed
        ) {
          this.openNodesFromParent(this.parentElements);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.dataSource) {
      this.dataSource = new TreeViewDataSource(
        this.treeControl,
        this.treeViewService,
        this.mediaType,
        this.forbiddenType
      );
    }

    this.nodeId = this.node.id;
    this.nodeCollapsed = false;
    if (
      changes['node'] &&
      changes['node'].currentValue &&
      changes['node'].currentValue !== changes['node'].previousValue
    ) {
      this.findTreeNode(this.node);
    } else {
      this.dataSource.data = [];
    }
  }

  ngAfterViewChecked() {
    const matchElement = this.eleRef?.nativeElement?.querySelector(
      `#node-${this.node?.id}-tree-child-node`
    );
    if (matchElement && this.isTravered === false) {
      this.isTravered = true;
      matchElement.scrollIntoView(true);
    }
  }

  logTreeControl(event, node: NodeEntry) {
    this.insertMediaService.setClickedFolderDetails(node);
    this.nodeService.setFolderId('');
    if (
      event.target.classList?.contains('wk-icon-folder') ||
      event.target.classList?.contains('wk-icon-chevron-right') ||
      event.target.classList?.contains('selected-node-folder') ||
      event.target.classList?.contains('adf-tree-view-label')
    ) {
      this.nodeService.setFolderId(node.entry.id);
    }
    const selectedNode = 'selected-node';
    const selectedFolder =
      this.eleRef.nativeElement.querySelectorAll('.mat-tree-node');
    if (selectedFolder) {
      selectedFolder.forEach((element) => {
        if (element.getAttribute('class').includes('selected-node')) {
          this.renderer.removeClass(element, selectedNode);
        }
      });
    }
  }

  onPreviewClicked(event) {
    event.stopPropagation();
    this.insertMediaService.openPreviewDialog(event);
  }

  onNodeClicked(event, node: NodeEntry) {
    const selectedChild = 'selected-child';
    let cloneData = [];
    if (this.mediaType === 'image') {
      this.selectedMediaNodes = this.treeViewService.getSelectedImageNodes(
        event,
        node,
        this.renderer
      );
    } else {
      if (this.lastSelectedAudioVideo) {
        this.renderer.removeClass(
          this.lastSelectedAudioVideo.target.closest('.child'),
          selectedChild
        );
      }
      if (
        this.selectedMediaNodes.length > 0 &&
        this.selectedMediaNodes[0] === node
      ) {
        this.lastSelectedAudioVideo = undefined;
        this.selectedMediaNodes = [];
      } else {
        this.renderer.addClass(event.target.closest('.child'), selectedChild);
        this.lastSelectedAudioVideo = event;
        this.selectedMediaNodes = [node];
      }
    }
    cloneData = JSON.parse(JSON.stringify(this.selectedMediaNodes));
    for (const mediaData of cloneData) {
      this.insertMediaService.selectedNode.set(mediaData.id, mediaData);
    }
  }

  getLevel = (node: TreeBaseNode) => node.level;

  isExpandable = (node: TreeBaseNode) => node.expandable;

  hasChild = (_: number, nodeData: TreeBaseNode) => nodeData.expandable;

  getMediaSource(id: string) {
    return this.contentApiService.getContentUrl(id);
  }

  private openNodesFromParent(parentNodes: PathElement[]) {
    let getParentElement = false;
    parentNodes.forEach((parent: PathElement) => {
      if (!getParentElement) {
        const treeNode = this.treeControl.dataNodes.find(
          (t) => t.nodeId === parent.id
        );
        if (treeNode) {
          this.treeControl.expand(treeNode);
          getParentElement = true;
          this.parentElements.splice(
            parentNodes.indexOf(parent),
            parentNodes.length
          );
        }
      }
    });
  }

  private findTreeNode(node: Node) {
    this.parentElements = [...node['path']['elements']];
    this.parentElements.push(node);
    if (this.treeControl.dataNodes) {
      const childTreeNodes = this.childrenIsAlreadyExpanded();
      if (childTreeNodes.length > 0) {
        this.nodeCollapsed = true;
        childTreeNodes.forEach((treeNode) =>
          this.treeControl.collapseDescendants(treeNode)
        );
      } else {
        this.parentElements.reverse();
        const parentElements = [...this.parentElements];
        this.openNodesFromParent(parentElements);
      }
    } else {
      const rootNode = this.parentElements.find(
        (root) => root.name === this.rootName
      );
      if (rootNode) {
        this.loadTreeNode(rootNode.id);
        this.parentElements.reverse();
      } else {
        this.parentElements = [];
      }
    }
  }

  private childrenIsAlreadyExpanded(): TreeBaseNode[] {
    return this.treeControl.dataNodes.filter(
      (treeNode) =>
        treeNode.node.entry.parentId === this.nodeId &&
        this.treeControl.isExpanded(treeNode)
    );
  }

  private loadTreeNode(nodeId: string) {
    if (this.mediaType === CommonConstants.applicationText) {
      this.treeViewService
        .getNodeChildren(nodeId)
        .pipe(
          map((data: ResultSetPaging): TreeBaseNode[] =>
            data.list.entries.map((node) => new TreeBaseNode(node as NodeEntry))
          ),
          takeUntil(this.onDestroy$)
        )
        .subscribe((treeNode: TreeBaseNode[]) => {
          this.dataSource.data = treeNode;
        });
    } else {
      this.treeViewService
        .getCustomTreeNodes(nodeId, this.mediaType, this.forbiddenType)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((treeNode: TreeBaseNode[]) => {
          this.dataSource.data = treeNode;
        });
    }
  }

  onPlay(event, node) {
    event.stopPropagation();
    node['isPlay'] = true;
    this.audio.src = this.getMediaSource(node.entry.id);
    this.audio.load();
    this.audio.play();
    if (this.previousNode?.entry?.id === node.entry.id) {
      this.previousNode = node;
    } else {
      if (this.previousNode) {
        this.previousNode['isPlay'] = false;
      }
      this.previousNode = node;
    }
    this.audio?.addEventListener('ended', () => {
      this.previousNode['isPlay'] = false;
    });
  }

  onPause(event, node) {
    event.stopPropagation();
    this.audio.pause();
    node['isPlay'] = false;
    this.previousNode = Object.create(null);
  }

  public getPoster(tenantId: string): string {
    return this.contentService.getPosterUrl(tenantId);
  }

  ngOnDestroy() {
    this.insertMediaService.setClickedFolderDetails('');
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.audio.pause();
  }
}
